import React from 'react';
import { ArrowRightIcon, ShieldCheckIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { FadeIn } from './FadeIn';
export function Hero() {
  return <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden" data-id="element-144">
      {/* Background Gradients & Blurs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-145">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-200/40 blur-[100px]" data-id="element-146" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-300/30 blur-[120px]" data-id="element-147" />
        <div className="absolute top-[20%] right-[10%] w-[30%] h-[30%] rounded-full bg-slate-200/50 blur-[80px]" data-id="element-148" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-149">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-150">
          {/* Text Content */}
          <div className="max-w-2xl" data-id="element-151">
            <FadeIn data-id="element-152">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-medium mb-6" data-id="element-153">
                <MapPinIcon className="w-4 h-4" data-id="element-154" />
                <span data-id="element-155">Serving Fresno & Surrounding Areas</span>
              </div>
            </FadeIn>

            <FadeIn delay={100} data-id="element-156">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight mb-6" data-id="element-157">
                Trusted{' '}
                <span className="text-gradient" data-id="element-158">Notary Public Fresno</span>{' '}
                Directory
              </h1>
            </FadeIn>

            <FadeIn delay={200} data-id="element-159">
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-160">
                Find the best{' '}
                <strong className="font-medium text-slate-800" data-id="element-161">
                  notary public in Fresno, CA
                </strong>
                . Compare top-rated mobile notary services with real reviews,
                24/7 availability, and verified credentials. Serving Woodward
                Park, Fig Garden, Tower District, Clovis, and all of Fresno
                County with professional notarization services.
              </p>
            </FadeIn>

            <FadeIn delay={300} data-id="element-162">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-163">
                <a href="#contact" className="inline-flex justify-center items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded-full text-base font-semibold transition-all hover:shadow-xl hover:-translate-y-1" data-id="element-164">
                  Find Notary Services
                  <ArrowRightIcon className="w-5 h-5" data-id="element-165" />
                </a>
                <a href="#services" className="inline-flex justify-center items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-8 py-4 rounded-full text-base font-semibold transition-all hover:shadow-md" data-id="element-166">
                  View Services
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={400} data-id="element-167">
              <div className="mt-10 pt-8 border-t border-slate-200" data-id="element-168">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-5" data-id="element-169">
                  Trusted & Certified
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl" data-id="element-170">
                  {/* NNA Badge */}
                  <div className="bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all flex flex-col items-center justify-center gap-2 min-h-[100px]" data-id="element-171">
                    <img src="https://www.nationalnotary.org/file%20library/nna/reference-library/state-law-summaries/images/nna-logo.png" alt="National Notary Association" className="h-8 w-auto object-contain" onError={e => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.classList.remove('hidden');
                  }} data-id="element-172" />
                    <div className="hidden text-center" data-id="element-173">
                      <div className="text-sm font-bold text-blue-900" data-id="element-174">NNA</div>
                      <div className="text-xs text-slate-500" data-id="element-175">Certified</div>
                    </div>
                  </div>

                  {/* California SOS Badge */}
                  <div className="bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all flex flex-col items-center justify-center gap-2 min-h-[100px]" data-id="element-176">
                    <img src="https://www.sos.ca.gov/administration/images/sos-seal.png" alt="California Secretary of State" className="h-12 w-auto object-contain" onError={e => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.classList.remove('hidden');
                  }} data-id="element-177" />
                    <div className="hidden text-center" data-id="element-178">
                      <div className="text-xs font-bold text-slate-900" data-id="element-179">
                        CA SOS
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-180">Commissioned</div>
                    </div>
                  </div>

                  {/* SnapDocs Badge */}
                  <div className="bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all flex flex-col items-center justify-center gap-2 min-h-[100px]" data-id="element-181">
                    <img src="https://snapdocs.com/wp-content/themes/snapdocs/images/logo.svg" alt="SnapDocs" className="h-6 w-auto object-contain" onError={e => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.classList.remove('hidden');
                  }} data-id="element-182" />
                    <div className="hidden text-center" data-id="element-183">
                      <div className="text-sm font-bold text-emerald-600" data-id="element-184">
                        SnapDocs
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-185">Verified</div>
                    </div>
                  </div>

                  {/* Licensed & Bonded Badge */}
                  <div className="bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all flex flex-col items-center justify-center gap-2 min-h-[100px]" data-id="element-186">
                    <ShieldCheckIcon className="h-10 w-10 text-blue-600" data-id="element-187" />
                    <div className="text-center" data-id="element-188">
                      <div className="text-xs font-bold text-slate-900" data-id="element-189">
                        Licensed
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-190">& Bonded</div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Image/Visual Placeholder */}
          <FadeIn delay={200} direction="left" className="relative lg:ml-auto w-full max-w-lg mx-auto lg:max-w-none" data-id="element-191">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl group" data-id="element-192">
              {/* Placeholder for actual image - using a nice gradient for now */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-800 to-blue-950 mix-blend-multiply opacity-90 transition-opacity group-hover:opacity-80 z-10" data-id="element-193" />
              <img src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Professional notary public signing a document" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-194" />

              {/* Floating Badge */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-md p-6 rounded-xl shadow-lg z-20 transform transition-transform group-hover:-translate-y-2" data-id="element-195">
                <div className="flex items-start gap-4" data-id="element-196">
                  <div className="bg-blue-100 p-3 rounded-full text-blue-700" data-id="element-197">
                    <ShieldCheckIcon className="w-6 h-6" data-id="element-198" />
                  </div>
                  <div data-id="element-199">
                    <h3 className="font-bold text-slate-900" data-id="element-200">
                      100% Secure & Confidential
                    </h3>
                    <p className="text-sm text-slate-600 mt-1" data-id="element-201">
                      Your sensitive documents are handled with the utmost care
                      and professionalism.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative dots */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[radial-gradient(#cbd5e1_2px,transparent_2px)] [background-size:10px_10px] -z-10" data-id="element-202" />
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[radial-gradient(#cbd5e1_2px,transparent_2px)] [background-size:10px_10px] -z-10" data-id="element-203" />
          </FadeIn>
        </div>
      </div>
    </section>;
}